import { css } from '@emotion/react';
import { CallManager } from '@helpers/CallManager';
import { Button, Row, Col, Alert } from 'antd';
import { useNotification } from '../../../contexts/NotificationContext/NotificationContext';
import { useEffect } from 'react';
import { BsCameraVideo, BsCheckCircle, BsTelephone } from 'react-icons/bs';
import { type SetTestCallStepType, type TestCallStep } from './ClinicianCallTestSession';
import { useTranslation } from 'react-i18next';
import TestCallResultsModal from './TestCallResultsModal';
import BlockedMediaPermissionsWarning from '@components/MediaPermissions/BlockedMediaPermissionsWarning';
import { checkAudioVideoEnabled } from '@lib/checkAudioVideoEnabled';

type TestCallInstructions = {
  testCallSignalingManager: CallManager | null;
  setTestCallStep: SetTestCallStepType;
  testCallStep: TestCallStep;
  setCallCompleted: (value: boolean) => void;
  setShowCallTestModal: (value: boolean) => void;
  showCallTestModal: boolean;
  isBlockedByMedia: boolean;
};

const InstructionsStyling = css`
  max-width: 800px;
  width: 100%;

  ul > li::marker,
  .icon {
    color: var(--productPrimary);
  }

  .icon {
    font-size: 16px;
    margin-right: 10px;
  }

  span.bold {
    font-weight: bold;
  }

  span.italic {
    font-style: italic;
  }

  .step-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .container {
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 8px;
  }

  .step-container {
    background-color: #fff;
  }

  .step-heading {
    margin-bottom: 10px;
  }

  .step-instructions {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    list-style-position: inside;
  }

  .step-instructions-detail {
    list-style: circle;
    padding: 0;
    margin: 10px 0;
    list-style-position: inside;

    li {
      padding: 5px;
    }
  }

  .step-button {
    padding: 10px 0;

    button {
      margin: 0 5px;
    }
  }
`;

const CircleStepCount = ({ value }: { value: string }) => (
  <div
    css={css`
      --size: 40px;
      display: flex;
      align-items: center;
      justify-content: center;

      font-size: 0.9rem;
      background-color: var(--productPrimary);
      width: var(--size);
      height: var(--size);
      border-radius: 50%;
      color: #fff;
    `}
  >
    {value}
  </div>
);

const StepContainer = ({ children, stepNumber }: { children: React.ReactNode; stepNumber: string }) => (
  <Row>
    <Col flex="55px">
      <CircleStepCount value={stepNumber} />
    </Col>
    <Col flex="auto">{children}</Col>
  </Row>
);

const TestCallInstructions = ({
  testCallSignalingManager: signalingManager,
  setTestCallStep,
  testCallStep,
  setCallCompleted,
  setShowCallTestModal,
  showCallTestModal,
  isBlockedByMedia
}: TestCallInstructions) => {
  const { t } = useTranslation();
  const { errorNotification, warningNotification } = useNotification();

  const withSignalingManagerCheck = (actionName: string, action: (manager: CallManager) => void) => {
    if (!signalingManager) {
      errorNotification({
        message: 'Connection Error',
        description: `Unable to ${actionName}. Please try again or refresh the page.`
      });
      return;
    }
    try {
      action(signalingManager);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('No remote participant connected')) {
        warningNotification({
          message: 'Automated patient not connected',
          description: 'Please try refreshing the page to reconnect to the automated patient.'
        });
      } else {
        throw err;
      }
    }
  };

  const checkCanStartRecording = () => {
    const { isVideoEnabled, isAudioEnabled } = checkAudioVideoEnabled();
    if (!isVideoEnabled || !isAudioEnabled) {
      errorNotification({
        message: t('Unable to start recording'),
        description: t(
          'Check if your video and audio are on to record. If this issue still exists, refresh the page and try again.'
        ),
        duration: 10
      });
    }
    return isVideoEnabled && isAudioEnabled;
  };

  useEffect(() => {
    if (!signalingManager) return;

    signalingManager.setHandlers({
      onPromptVideoComplete: () => {
        setTestCallStep('ready_to_record');
      },
      onRecordingStarted: () => {
        setTestCallStep('recording');
      },
      onRecordingStopped: (_senderId, data) => {
        setTestCallStep(data?.hasRecording ? 'ready_to_replay' : 'waiting_for_prompt');
      },
      onReplayComplete: () => {
        setTestCallStep('replayed');
      },
      onEndRecordingSession: () => {},
      onThankYouVideoComplete: () => {
        setTestCallStep('complete');
        setCallCompleted(true);
        setShowCallTestModal(true);
      }
    });
    return () => {
      signalingManager.setHandlers({});
    };
  }, [signalingManager]);

  const handleCompleteTest = () => {
    withSignalingManagerCheck('complete test', (manager) => {
      manager.endRecordingSession();
      setTestCallStep('complete');
    });
  };

  const handleStartRecording = () => {
    if (checkCanStartRecording()) {
      withSignalingManagerCheck('start recording', (manager) => manager.startRecording());
    }
  };

  const canStartRecording = ['ready_to_record', 'ready_to_replay', 'replayed'].includes(testCallStep);
  const canEndRecording = testCallStep === 'recording';
  const isRecording = testCallStep === 'recording';
  const canReplayRecording = testCallStep === 'ready_to_replay' || testCallStep === 'replayed';
  const hasReplayedRecording = testCallStep === 'replayed';
  const isTestComplete = testCallStep === 'complete';

  return (
    <div css={InstructionsStyling}>
      {isBlockedByMedia && (
        <BlockedMediaPermissionsWarning handleDismiss={() => {}} handleRefreshPage={() => window.location.reload()} />
      )}
      <h3>{t('Test call instructions')}</h3>
      <Alert
        className="container"
        showIcon
        type="info"
        description={t(
          'Video recordings are not retained unless an issue occurs. In such cases, the video will be saved to help diagnose problems and will be deleted afterwards.'
        )}
      />
      <ul className="step-list">
        <li className="container step-container">
          <StepContainer stepNumber="1">
            <Row align={'middle'} className="step-heading">
              <BsTelephone className="icon" />
              {t('Start the test call')}
            </Row>
            <Row>
              <div className="step-instructions">
                <ul className="step-instructions-detail">
                  <li>
                    {t('Check if the patient is')} <span className="bold">{t('online')}</span> {t('in the panel')}
                  </li>
                  <li>
                    {t('Select the')} <span className="bold">{t("'Test Call'")}</span> {t('button')}
                  </li>
                  <li>
                    {t('Choose your')} <span className="bold">{t('call type')}</span> {t('from the dropdown')}
                  </li>
                </ul>
              </div>
            </Row>
          </StepContainer>
        </li>

        <li className="container step-container">
          <StepContainer stepNumber="2">
            <Row align={'middle'} className="step-heading">
              <BsCameraVideo className="icon" />
              {t('During the test call')}
            </Row>
            <Row>
              <ol className="step-instructions">
                <li>
                  {t('Make sure your')}
                  <span className="bold">{t(' audio ')}</span>
                  {t('and')}
                  <span className="bold">{t(' video ')}</span>
                  {t(' are on')}
                </li>
                <li className="step-button">
                  {t('Once enabled, click')}
                  <Button
                    type="default"
                    data-testid="start-test-recording"
                    disabled={!canStartRecording || isTestComplete}
                    onClick={handleStartRecording}
                    loading={isRecording}
                  >
                    {t('Start Recording')}
                  </Button>
                </li>

                <li>
                  {t('Complete these actions (maximum of 30 seconds)')}
                  <ul className="step-instructions-detail">
                    <li>
                      <span className="bold">{t('Say')}:</span>
                      <span className="italic"> {t("my name is 'your name'. Today's date is 'date'")}</span>
                    </li>
                    <li>
                      <span className="bold">{t('Wave')}:</span> {t('both hands at the camera')}
                    </li>
                    <li>
                      <span className="bold">{t('Point out')}:</span> {t('an object behind you')}
                    </li>
                  </ul>
                </li>

                <li className="step-button">
                  {t('Click')}
                  <Button
                    type="default"
                    data-testid="stop-test-recording"
                    disabled={!canEndRecording || isTestComplete}
                    onClick={() => withSignalingManagerCheck('stop recording', (manager) => manager.stopRecording())}
                    loading={false}
                  >
                    {t('Stop Recording')}
                  </Button>
                  {t('when finished')}
                </li>
              </ol>
            </Row>
          </StepContainer>
        </li>

        <li className="container step-container">
          <StepContainer stepNumber="3">
            <Row align={'middle'} className="step-heading">
              <BsCheckCircle className="icon" />
              {t('After the test')}
            </Row>
            <Row>
              <ul className="step-instructions">
                <li className="step-button">
                  {t('Click')}
                  <Button
                    type="default"
                    data-testid="replay-test-recording"
                    disabled={!canReplayRecording || isTestComplete}
                    onClick={() =>
                      withSignalingManagerCheck('replay recording', (manager) => manager.replayRecording())
                    }
                    loading={false}
                  >
                    {t('Replay Recording')}
                  </Button>
                  {t('to view the test video')}
                </li>
                <li>
                  {t('Check the')}
                  <span className="bold">{t(' audio ')}</span>
                  {t('and')}
                  <span className="bold">{t(' video ')}</span>
                  {t(' quality of the recording')}
                </li>
                <li className="step-button">
                  {t('Once you are done, click')}
                  <Button
                    type="default"
                    data-testid="complete-test-recording"
                    disabled={isTestComplete || !hasReplayedRecording}
                    onClick={handleCompleteTest}
                    loading={false}
                  >
                    {t('Complete Test')}
                  </Button>
                </li>
                <li>{t('Fill out the feedback form when prompted')}</li>
              </ul>
            </Row>
          </StepContainer>
        </li>
      </ul>
      <TestCallResultsModal open={showCallTestModal} onClose={() => setShowCallTestModal(false)} />
    </div>
  );
};

export default TestCallInstructions;
